<template>
  <div>

    <h4 class="section-title">Assinatura</h4>

    <div class="row">
      <div class="col-md-4">
        <div class="form-group">
          <label for="account-subscription-type">Status da conta</label>
          <select v-model="account.subscription_type" id="account-subscription-type"
                  @change="onStatusChange" class="form-control">
            <option v-if="!isKnownStatus" :value="account.subscription_type">
              {{ account.subscription_type || 'Sem status' }}
            </option>
            <option v-for="option in statusOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="col-md-4">
        <div class="form-group">
          <label for="account-type">Tipo da conta</label>
          <select v-model="account.account_type" id="account-type" class="form-control">
            <option v-if="!isKnownAccountType" :value="account.account_type">
              {{ account.account_type }}
            </option>
            <option v-for="option in accountTypeOptions" :key="String(option.value)" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="col-md-4">
        <div class="form-group">
          <label for="account-step">Etapa do cadastro</label>
          <input v-model.number="step" id="account-step" type="number" min="0" class="form-control">
        </div>
      </div>

      <template v-if="showNoticeFields">
        <div class="col-md-4">
          <div class="form-group">
            <label for="account-notice-mode">Aviso prévio <span class="required">*</span></label>
            <select v-model="account.subscription_notice_mode" id="account-notice-mode"
                    @change="onNoticeModeChange" class="form-control">
              <option :value="null">Selecione</option>
              <option :value="NOTICE_ABSENT">Aviso prévio ausente</option>
              <option :value="NOTICE_WITH_DATE">Com aviso prévio</option>
            </select>
          </div>
        </div>

        <div v-if="account.subscription_notice_mode === NOTICE_WITH_DATE" class="col-md-4">
          <div class="form-group">
            <label for="account-notice-end">Aviso prévio válido até <span class="required">*</span></label>
            <input :value="account.subscription_notice_end_date"
                   :min="today"
                   @change="setNoticeEndDate($event.target.value)"
                   @blur="checkNoticeEndDate"
                   id="account-notice-end" type="date" class="form-control">
          </div>
        </div>
      </template>

      <div v-if="canRetain" class="col-md-4">
        <div class="form-group">
          <label>Retenção</label>
          <button @click="retain" class="btn btn-outline retain-button">
            Reter cliente e reativar conta
          </button>
        </div>
      </div>

      <div v-if="deletionLabel" class="col-md-4">
        <div class="form-group">
          <label>Exclusão da conta</label>
          <p class="deletion-info" :class="{ 'deletion-due': daysToDeletion <= 0 }">
            {{ deletionLabel }}
          </p>
        </div>
      </div>
    </div>

    <h4 class="section-title section-divided">Contrato principal</h4>

    <div class="row">
      <div class="col-md-4">
        <div class="form-group">
          <label for="contract-start">Vigência a partir de</label>
          <input v-model="contractDocument.start_date" id="contract-start" type="date" class="form-control">
        </div>
      </div>

      <div class="col-md-4">
        <div class="form-group">
          <label for="contract-renewal">Vigência até</label>
          <select v-model="contractDocument.renewal_mode" id="contract-renewal"
                  @change="onRenewalChange" class="form-control">
            <option value="automatic">Renovado automaticamente</option>
            <option value="date">Inserir data</option>
          </select>
        </div>
      </div>

      <div v-if="contractDocument.renewal_mode === 'date'" class="col-md-4">
        <div class="form-group">
          <label for="contract-end">Data de encerramento</label>
          <input v-model="contractDocument.end_date" id="contract-end" type="date" class="form-control">
        </div>
      </div>
    </div>

    <FileCard v-if="contractDocument.file"
              :file="contractDocument.file"
              @open="openFile(contractDocument.file)"
              @replace="setContractFile"
              @remove="removeContractFile"/>

    <Upload v-else class="upload" :is-multiple="false"
            accept="application/pdf,.pdf" :method="setContractFile">
      <upload-cloud-icon size="2x" class="upload-icon"/>
      <p class="mb-0">Selecione ou arraste o PDF do contrato</p>
    </Upload>

    <div class="additives-head">
      <h4 class="section-title mb-0">
        Aditivos contratuais
        <span class="count">{{ additives.length }}</span>
      </h4>

      <button @click="openNew" class="btn btn-outline">
        <plus-icon size="1x" class="mr-1"/>
        Aditivo
      </button>
    </div>

    <p v-if="!additives.length" class="empty">
      Nenhum aditivo registrado para esta conta.
    </p>

    <div v-for="(additive, index) in additives" :key="additive.id" class="additive">
      <span class="additive-order">{{ index + 1 }}º</span>

      <div class="additive-main">
        <span class="additive-title">{{ additive.description }}</span>
        <span class="additive-dates">
          Assinado em {{ formatDate(additive.signed_at) }} ·
          vigência a partir de {{ formatDate(additive.effective_from) }}
        </span>
      </div>

      <div class="additive-file">
        <button v-if="additive.file" @click="openFile(additive.file)" class="btn btn-link file-link">
          <file-text-icon size="1x" class="mr-1"/>
          {{ additive.file.name }}
        </button>
        <span v-else class="no-file">sem arquivo</span>
      </div>

      <button @click="openEdit(additive, index)" class="icon-button" title="Editar">
        <edit-2-icon size="1x"/>
      </button>

      <button @click="removeAdditive(index)" class="icon-button danger" title="Remover">
        <trash-2-icon size="1x"/>
      </button>
    </div>

    <AdditiveModal v-model="showModal" :additive="editing" @save="saveAdditive"/>

  </div>
</template>

<script>
  import { Edit2Icon, FileTextIcon, PlusIcon, Trash2Icon, UploadCloudIcon } from 'vue-feather-icons'

  import Upload from '@/components/shares/specialTag/Upload'
  import AdditiveModal from '@/components/admin/accounts/show/AdditiveModal'
  import FileCard from '@/components/admin/accounts/show/FileCard'
  import { ACCOUNT_STATUS, ACCOUNT_STATUS_OPTIONS, isAccountStatusOption } from '@/utils/accountStatus'
  import {
    ABSENT_NOTICE_APPLIED,
    NOTICE_ABSENT,
    NOTICE_WITH_DATE,
    applyNoticeMode,
    canRetain,
    daysUntil,
    deletionDateFor,
    deletionLabelFor,
    hasNoticeRecord,
    noticeDateError,
    partialDate,
    todayISO
  } from '@/models/accountRetention'
  import {
    FILE_UNAVAILABLE_ERROR,
    PDF_ERROR,
    discardPendingFile,
    isPdf,
    stagePendingFile
  } from '@/models/accountContract'

  const ACCOUNT_TYPE_OPTIONS = [
    { value: null, label: 'Cliente' },
    { value: 'partner', label: 'Parceira' }
  ]

  export default {
    components: {
      AdditiveModal, Edit2Icon, FileCard, FileTextIcon,
      PlusIcon, Trash2Icon, Upload, UploadCloudIcon
    },
    computed: {
      accountTypeOptions() {
        return ACCOUNT_TYPE_OPTIONS
      },
      additives() {
        return this.account.contract_additives
      },
      contractDocument() {
        return this.account.contract_document
      },
      daysToDeletion() {
        return daysUntil(deletionDateFor(this.account.deletion_base_at))
      },
      deletionLabel() {
        return deletionLabelFor(this.account.deletion_base_at)
      },
      canRetain() {
        return canRetain(this.account)
      },
      NOTICE_ABSENT() {
        return NOTICE_ABSENT
      },
      NOTICE_WITH_DATE() {
        return NOTICE_WITH_DATE
      },
      showNoticeFields() {
        return this.account.subscription_type === ACCOUNT_STATUS.IN_NOTICE ||
          hasNoticeRecord(this.account)
      },
      isKnownAccountType() {
        const value = this.account.account_type
        return !value || ACCOUNT_TYPE_OPTIONS.some(option => option.value === value)
      },
      isKnownStatus() {
        return isAccountStatusOption(this.account.subscription_type)
      },
      statusOptions() {
        return ACCOUNT_STATUS_OPTIONS
      },
      today() {
        return todayISO()
      },
      step: {
        get() {
          return this.account.step
        },
        set(value) {
          this.account.step = value

          if (this.account.metadata)
            this.$set(this.account.metadata, 'step', value)
        }
      }
    },
    data() {
      return {
        editing: null,
        editingIndex: null,
        sequence: 0,
        showModal: false
      }
    },
    methods: {
      formatDate(value) {
        return value ? this.$options.filters.date(value) : '-'
      },
      openFile(file) {
        if (file.pending_token)
          return this.$notify.add('O arquivo só pode ser aberto depois de salvar.')

        if (!file.url)
          return this.$notify.addError(FILE_UNAVAILABLE_ERROR)

        window.open(file.url, '_blank')
      },
      openEdit(additive, index) {
        this.editing = additive
        this.editingIndex = index
        this.showModal = true
      },
      openNew() {
        this.editing = null
        this.editingIndex = null
        this.showModal = true
      },
      onNoticeModeChange() {
        applyNoticeMode(this.account)

        if (this.account.subscription_notice_mode === NOTICE_ABSENT)
          this.$notify.add(ABSENT_NOTICE_APPLIED)
      },
      onStatusChange() {
        const status = this.account.subscription_type

        if (status === ACCOUNT_STATUS.CANCELED) {
          if (!this.account.deletion_base_at)
            this.account.deletion_base_at = todayISO()

          return
        }

        if (status !== ACCOUNT_STATUS.IN_NOTICE)
          this.account.deletion_base_at = null
      },
      onRenewalChange() {
        if (this.contractDocument.renewal_mode === 'automatic')
          this.contractDocument.end_date = null
      },
      retain() {
        if (!confirm('Reter o cliente e voltar a conta para Ativa? O aviso prévio e a contagem para exclusão serão descartados.'))
          return

        this.$emit('retain')
      },
      removeAdditive(index) {
        const additive = this.additives[index]

        if (!confirm(`Remover o aditivo "${additive.description}"?`))
          return

        discardPendingFile(additive.file)
        this.additives.splice(index, 1)
      },
      removeContractFile() {
        if (!confirm('Remover o PDF do contrato?'))
          return

        discardPendingFile(this.contractDocument.file)
        this.$set(this.contractDocument, 'file', null)
      },
      setNoticeEndDate(value) {
        if (!value) {
          this.account.subscription_notice_end_date = null
          return
        }

        if (partialDate(value))
          return

        this.account.subscription_notice_end_date = value
        applyNoticeMode(this.account)
      },
      checkNoticeEndDate() {
        const invalid = noticeDateError(this.account.subscription_notice_end_date)

        if (invalid)
          this.$notify.addError(invalid)
      },
      saveAdditive({ additive, file }) {
        const record = { ...additive }

        if (file) {
          discardPendingFile(record.file)
          record.file = stagePendingFile(file)
        }

        if (this.editingIndex === null) {
          this.sequence += 1
          record.id = `new_${this.sequence}`
          this.additives.push(record)
        } else {
          this.$set(this.additives, this.editingIndex, record)
        }

        this.editing = null
        this.editingIndex = null
      },
      setContractFile(files) {
        const file = files[0]

        if (!file)
          return

        if (!isPdf(file))
          return this.$notify.addError(PDF_ERROR)

        discardPendingFile(this.contractDocument.file)
        this.$set(this.contractDocument, 'file', stagePendingFile(file))
      }
    },
    name: 'TabContract',
    props: {
      account: { required: true, type: Object }
    }
  }
</script>

<style scoped>
  .section-title {
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 600;
    color: var(--primary-text-color);
  }

  .section-divided {
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid var(--border-color);
  }

  .required {
    color: #d9534f;
  }

  .retain-button {
    display: block;
    width: 100%;
  }

  .deletion-info {
    margin: 0;
    padding-top: 7px;
    font-size: 14px;
    font-weight: 600;
    color: var(--primary-text-color);
  }

  .deletion-due {
    color: #d9534f;
  }

  .count {
    padding: 1px 8px;
    margin-left: 4px;
    border-radius: 1rem;
    background: var(--light-gray);
    font-size: 12px;
    font-weight: 400;
    color: var(--secondary-text-color);
  }

  .upload {
    position: relative;
    z-index: 1;
  }

  .upload-icon {
    color: var(--icon-color);
  }

  .additives-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid var(--border-color);
    margin-bottom: 16px;
  }

  .empty {
    font-size: 13px;
    color: var(--secondary-text-color);
  }

  .additive {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 16px;
    margin-bottom: 8px;
    border: 1px solid var(--border-color);
    border-radius: 5px;
    background: var(--card-bg);
  }

  .additive-order {
    font-size: 13px;
    font-weight: 600;
    color: var(--secondary-text-color);
  }

  .additive-main {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .additive-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--primary-text-color);
  }

  .additive-dates {
    font-size: 12px;
    color: var(--secondary-text-color);
  }

  .file-link {
    display: flex;
    align-items: center;
    padding: 0;
    font-size: 13px;
  }

  .no-file {
    font-size: 12px;
    color: var(--text-muted);
  }

  .icon-button {
    padding: 4px;
    border: none;
    background: none;
    color: var(--icon-color);
    cursor: pointer;
  }

  .icon-button:hover {
    color: var(--primary-text-color);
  }

  .icon-button.danger:hover {
    color: #d9534f;
  }
</style>
